import type { Capability } from '@floorpoll/shared';
import type { AnchorId } from './anchors.js';

/**
 * The guide's script.
 *
 * Data only — the provider owns sequencing and the overlay owns rendering, so
 * rewording a step or reordering the run never touches either.
 *
 * It runs in two chapters, because a brand-new poll is created with no screens
 * (`repos/quizzes.ts`) and there is nothing in the builder to point at until the
 * user makes one. Step 4 hands off; the guide stands by; the builder chapter
 * resumes on arrival.
 *
 * What it covers is chosen against the public pitch: the landing page and
 * `AuthShowcase` already say "Build it / Open the room / Watch it land", so
 * the script skips that and takes the rules those surfaces have no room for —
 * the structural caps, the freeze while live, and what Reset destroys.
 */

export type TourRoute = 'polls' | 'builder';

export interface TourStep {
  /** Stable key, used for React keys and tests. Not shown. */
  readonly id: string;
  /** Element to point at. Omitted for a centered card with no spotlight. */
  readonly anchor?: AnchorId;
  readonly title: string;
  readonly body: string;
  /** A quieter line under the body. Used once, to close the run. */
  readonly note?: string;
  readonly route: TourRoute;
  /** Dropped from the run when the viewer lacks this capability. */
  readonly needs?: Capability;
  /** Advances as soon as this anchor registers, so doing the thing is the Next button. */
  readonly advanceOnAnchor?: AnchorId;
}

export const TOUR_STEPS: readonly TourStep[] = [
  {
    id: 'welcome',
    route: 'polls',
    title: 'Welcome to FloorPoll',
    body: 'A short pass through building a poll and putting it in front of a room. Leave any time by pressing Escape.',
  },
  {
    id: 'workspace',
    route: 'polls',
    anchor: 'workspace-switcher',
    title: 'Your workspace',
    body: "Every poll lives in a workspace. Invite your team from People and they'll see the same polls you do.",
  },
  {
    id: 'name-it',
    route: 'polls',
    anchor: 'polls-create-field',
    needs: 'quiz:write',
    title: 'Name it first',
    body: 'A poll starts with a title. Its six-character join code is minted at the same moment.',
  },
  {
    id: 'handoff',
    route: 'polls',
    anchor: 'polls-create-submit',
    needs: 'quiz:write',
    title: "Create it and I'll follow",
    body: 'The guide stands by while you make your first poll, then picks up again in the builder.',
  },
  {
    id: 'heading',
    route: 'builder',
    anchor: 'poll-heading',
    title: 'Title and join code',
    body: "Rename a poll freely while it's a draft. The code underneath is what the room types in.",
  },
  {
    id: 'add-screen',
    route: 'builder',
    anchor: 'poll-add-screen',
    needs: 'quiz:write',
    title: 'Add a screen',
    body: 'A screen is one page of the big screen — what the room looks at together.',
    advanceOnAnchor: 'poll-screen',
  },
  {
    id: 'screen-rules',
    route: 'builder',
    anchor: 'poll-screen',
    title: 'Three questions, ten options',
    body: 'A screen holds one to three questions so it stays readable from the back of the room. Each question takes two to ten options.',
  },
  {
    id: 'theme',
    route: 'builder',
    anchor: 'poll-theme',
    needs: 'quiz:write',
    title: 'Make it yours',
    body: 'Your colors and logo carry to the big screen and to every phone in the room.',
  },
  {
    id: 'save',
    route: 'builder',
    anchor: 'poll-save',
    needs: 'quiz:write',
    title: 'Save before you present',
    body: 'Saving checks the structure. Every screen needs at least one question before a poll can go live.',
  },
  {
    id: 'present',
    route: 'builder',
    anchor: 'poll-present',
    needs: 'quiz:present',
    title: 'Going live',
    body: 'Present activates the join code and freezes editing. Ending the presentation releases both.',
  },
  {
    id: 'after',
    route: 'builder',
    title: 'After the show',
    body: 'Votes accumulate across presentations, so a poll can run again next week and keep counting. Reset zeroes the counts and lets a phone vote a second time.',
    note: 'Replay this guide any time from the foot of the page.',
  },
];

/** Steps this viewer can actually follow. A Viewer is never told to create things. */
export function stepsFor(can: (capability: Capability) => boolean): readonly TourStep[] {
  return TOUR_STEPS.filter((step) => !step.needs || can(step.needs));
}

/**
 * `/polls/:quizId` only. Results and presenter controls live one segment deeper
 * and are deliberately not part of the run.
 */
export function routeMatches(route: TourRoute, pathname: string): boolean {
  if (route === 'polls') return pathname === '/polls';
  return /^\/polls\/[^/]+$/.test(pathname);
}

const CONSOLE_PATHS = new Set(['/polls', '/people', '/settings', '/account', '/usage']);

/**
 * Whether the guide may draw here at all.
 *
 * The overlay mounts above the router so a run survives navigation, which means
 * it also has to decline the pages it has no business on: the audience-facing
 * join and big-screen views, and the presenter controls. That last one matters
 * most — the controls are reachable directly by URL during an event, and the
 * README's reasoning about terms bumps applies exactly: nothing goes over a
 * presenter mid-show.
 */
export function isGuideSurface(pathname: string): boolean {
  if (/^\/polls\/[^/]+\/present$/.test(pathname)) return false;
  return CONSOLE_PATHS.has(pathname) || /^\/polls\/[^/]+(\/results)?$/.test(pathname);
}
